import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../../auth/AuthContext';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import {
  RPA_LOGIN_ACTIONS,
  type RpaLoginActionCommand,
  type RpaLoginActionId,
  type RpaLoginActionResponse,
  type RpaLoginActionStatus,
  type RpaLoginActionStatusResponse,
  type RpaLoginEc2Status,
} from './rpaLoginConfig';
import { fetchRpaLoginStatuses, runRpaLoginAction } from './rpaLoginService';
import './RpaLoginScreen.css';

type RpaLoginScreenProps = {
  fetchStatuses?: (token: string) => Promise<RpaLoginActionStatusResponse>;
  runAction?: (
    token: string,
    actionId: RpaLoginActionId,
    command: RpaLoginActionCommand,
  ) => Promise<RpaLoginActionResponse>;
};

type ActionResult = {
  status: RpaLoginEc2Status | 'error';
  message: string;
};

export default function RpaLoginScreen({
  fetchStatuses = fetchRpaLoginStatuses,
  runAction = runRpaLoginAction,
}: RpaLoginScreenProps) {
  const { fetchJWT } = useAuthContext();
  const [activeActionId, setActiveActionId] = useState<RpaLoginActionId | null>(null);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [statuses, setStatuses] = useState<RpaLoginActionStatus[]>([]);

  const updateStatus = (nextStatus: RpaLoginActionStatus) => {
    setStatuses((currentStatuses) =>
      currentStatuses.map((currentStatus) =>
        currentStatus.action === nextStatus.action ? nextStatus : currentStatus,
      ),
    );
  };

  const loadStatuses = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setIsLoadingStatuses(true);
      }
      try {
        const { idToken } = await fetchJWT();
        if (!idToken) throw new Error('Missing auth token');

        const response = await fetchStatuses(idToken);
        setStatuses(response.actions);
      } catch (_error) {
        setActionResult({
          status: 'error',
          message: 'Unable to load RPA machine statuses.',
        });
      } finally {
        if (showLoading) {
          setIsLoadingStatuses(false);
        }
      }
    },
    [fetchJWT, fetchStatuses],
  );

  useEffect(() => {
    void loadStatuses();
  }, [loadStatuses]);

  useEffect(() => {
    if (!statuses.some((item) => item.status === 'starting' || item.status === 'shuttingDown')) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadStatuses(false);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [loadStatuses, statuses]);

  const handleActionCommand = async (
    status: RpaLoginActionStatus,
    command: RpaLoginActionCommand,
  ) => {
    const transitionStatus: RpaLoginEc2Status =
      command === 'start' ? 'starting' : 'shuttingDown';

    setActiveActionId(status.action);
    updateStatus({ ...status, status: transitionStatus });
    setActionResult({
      status: transitionStatus,
      message:
        command === 'start'
          ? `${status.label} is starting.`
          : `${status.label} is shutting down.`,
    });

    try {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Missing auth token');

      const response = await runAction(idToken, status.action, command);
      updateStatus(response);
      setActionResult({
        status: response.status,
        message: response.message,
      });
    } catch (_error) {
      setActionResult({
        status: 'error',
        message:
          command === 'start'
            ? `Unable to start ${status.label}.`
            : `Unable to shut down ${status.label}.`,
      });
    } finally {
      setActiveActionId(null);
    }
  };

  const statusesByAction = new Map(statuses.map((status) => [status.action, status]));
  const orderedStatuses = RPA_LOGIN_ACTIONS.flatMap((action) => {
    const status = statusesByAction.get(action.id);
    return status ? [status] : [];
  });

  return (
    <PageTemplate title="RPA Login">
      {actionResult ? (
        <div
          className={
            actionResult.status === 'error' ? 'govuk-error-summary' : 'govuk-inset-text'
          }
          role={actionResult.status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {actionResult.message}
        </div>
      ) : null}
      {isLoadingStatuses ? <p className="govuk-body">Loading statuses</p> : null}
      {!isLoadingStatuses ? (
        <table className="govuk-table rpa-login-status-table">
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              <th scope="col" className="govuk-table__header">
                Machine
              </th>
              <th scope="col" className="govuk-table__header">
                Status
              </th>
              <th scope="col" className="govuk-table__header">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="govuk-table__body">
            {orderedStatuses.map((status) => {
              return (
                <tr className="govuk-table__row" key={status.action}>
                  <th scope="row" className="govuk-table__header">
                    {status.label}
                  </th>
                  <td className="govuk-table__cell">
                    <span className="rpa-login-status-cell">
                      <StatusTag status={status.status} />
                      {status.status === 'starting' || status.status === 'shuttingDown' ? (
                        <span
                          aria-hidden="true"
                          className="rpa-login-spinner"
                          data-testid={`rpa-status-spinner-${status.action}`}
                        />
                      ) : null}
                    </span>
                  </td>
                  <td className="govuk-table__cell">
                    <ActionButton
                      activeActionId={activeActionId}
                      status={status}
                      onCommand={handleActionCommand}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </PageTemplate>
  );
}

type StatusTagProps = {
  status: RpaLoginEc2Status;
};

const statusDisplay: Record<RpaLoginEc2Status, { label: string; className: string }> = {
  running: {
    label: 'Running',
    className: 'govuk-tag--green',
  },
  offline: {
    label: 'Offline',
    className: 'govuk-tag--red',
  },
  starting: {
    label: 'Starting',
    className: 'govuk-tag--yellow',
  },
  shuttingDown: {
    label: 'Shutting down',
    className: 'govuk-tag--yellow',
  },
};

function StatusTag({ status }: StatusTagProps) {
  const display = statusDisplay[status];
  return <strong className={`govuk-tag ${display.className}`}>{display.label}</strong>;
}

type ActionButtonProps = {
  activeActionId: RpaLoginActionId | null;
  status: RpaLoginActionStatus;
  onCommand: (status: RpaLoginActionStatus, command: RpaLoginActionCommand) => void;
};

function ActionButton({ activeActionId, status, onCommand }: ActionButtonProps) {
  if (status.status === 'starting' || status.status === 'shuttingDown') {
    return (
      <button className="govuk-button govuk-button--secondary" disabled type="button">
        Please wait
      </button>
    );
  }

  if (status.status === 'running') {
    return (
      <button
        className="govuk-button govuk-button--warning"
        data-module="govuk-button"
        disabled={activeActionId === status.action}
        type="button"
        onClick={() => onCommand(status, 'stop')}
      >
        Shut down
      </button>
    );
  }

  return (
    <button
      className="govuk-button"
      data-module="govuk-button"
      disabled={activeActionId === status.action}
      type="button"
      onClick={() => onCommand(status, 'start')}
    >
      Start
    </button>
  );
}
